import * as React from "react";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { MoneyType, View } from "../../../api/api";
import { useStaticContext } from "../../../core";
import { triggerDefaultViewChangeRequest } from "../../../core/containers/Keypad/Common/Keypad.actions";
import {
  triggerBetDenominationChangeRequest,
  triggerBetLevelChangeRequest
} from "../../../core/containers/Money/Money.actions";
import {
  formatCash as formatCashWithConfiguration,
  formatCashLabel
} from "../../../core/utils/formatters";
import { desktop } from "../../../ui/_themes/constants";
import { themeColors } from "../../../ui/_themes/constants/colors";
import { IState } from "../../redux";
import { BetInCash, IBetInCashProps, IBetInfo } from "./BetInCash";
import { BetInCoins, IBetInCoinsProps } from "./BetInCoins";
import * as helpers from "../../../ui/_themes/helpers";

const calculateBetsInCashInfo = ({
  denominations,
  size,
  levels
}): IBetInfo[] => {
  const possibleResults = levels
    .map(level => {
      return denominations.map(denomination => ({
        level,
        denomination,
        value: size * level * denomination
      }));
    })
    .reduce((x, y) => x.concat(y), []);

  const accumulate = (accumulator, currentValue) => {
    if (!accumulator.find(obj => obj.value === currentValue.value)) {
      accumulator.push(currentValue);
    }

    return accumulator;
  };

  const uniqueResults = possibleResults.reduce(accumulate, [
    possibleResults[0]
  ]);

  uniqueResults.sort((a, b) => (a.value < b.value ? -1 : 1));

  return uniqueResults;
};

const BetLevelView = ({ className }) => {
  const dispatch = useDispatch();
  const { language } = useStaticContext();

  const { money } = useSelector((state: IState) => state);
  const view = useSelector((state: IState) => state.view);
  const shouldRender = view === View.BET_SETTINGS;

  const {
    moneyType,
    currencyFormat,
    denomination,
    denominations,
    level: selectedBetLevel,
    levels: betLevels,
    size
  } = money;

  const betsInCashInfo = useMemo(
    () =>
      calculateBetsInCashInfo({
        denominations,
        size,
        levels: betLevels
      }),
    [denominations, size, selectedBetLevel]
  );
  const selectedBetInfo = {
    denomination,
    level: selectedBetLevel,
    value: selectedBetLevel * size * denomination
  };

  if (!shouldRender) {
    return null;
  }

  const IS_CASH = moneyType === MoneyType.CASH;
  const IS_COINS = moneyType === MoneyType.COINS;

  // In cents
  const selectedCoinValue = denomination;
  const coinValues = denominations;

  const onClose = () => dispatch(triggerDefaultViewChangeRequest());

  const onBetLevelChanged = level =>
    dispatch(triggerBetLevelChangeRequest(level));

  const onCoinValueChanged = value =>
    dispatch(triggerBetDenominationChangeRequest(value));

  const onDenominationChanged = denomination =>
    dispatch(triggerBetDenominationChangeRequest(denomination));

  const formatCash = cash => formatCashWithConfiguration(cash, currencyFormat);

  const betInCashTitle = formatCashLabel(
    language.BetSettingsCashTitle.toUpperCase(),
    currencyFormat
  );

  const onBetInfoChanged = betInfo => {
    onDenominationChanged(betInfo.denomination);
    onBetLevelChanged(betInfo.level);
  };

  const coinValueTitle = language.CoinValue;

  const betInCashProps: IBetInCashProps = {
    title: betInCashTitle,
    betsInfo: betsInCashInfo,
    selectedBetInfo,
    formatter: formatCash,
    onBetInfoChanged,
    onClose
  };

  const betInCoinsProps: IBetInCoinsProps = {
    title: language.BetSettingsCashTitle,
    coinValueTitle,
    coinValues,
    selectedCoinValue,
    betLevelTitle: language.Level,
    betLevels,
    selectedBetLevel,
    formatter: formatCash,
    onCoinValueChanged,
    onBetLevelChanged,
    onClose
  };

  return (
    <div className={className}>
      {IS_CASH && <BetInCash {...betInCashProps} />}
      {IS_COINS && <BetInCoins {...betInCoinsProps} />}
    </div>
  );
};

export const BetLevel = styled(BetLevelView)`
  overflow: hidden;
  pointer-events: all;
  height: 100%;
  width: 100%;
  background-color: ${helpers.getBackgroundColor("settingsWindow")};

  &:before {
    background-color: ${themeColors.primaryDark};
    position: fixed;
    content: "";
    bottom: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    top: 0;
  }

  @media (min-device-width: 80em) {
    padding-bottom: ${desktop.keypadWrapper.height};
    height: 100%;
    min-height: 100%;
  }
`;
